import React, { useState, useEffect } from 'react';
import '../App.css';
import Header from './Header';
import Footer from './Footer';
import image1 from '../assets/flexibility.png';
import image2 from '../assets/social preferences.png';
import image3 from '../assets/work environment.png';
import image4 from '../assets/work style.png';
import image5 from '../assets/growth.png';
import principleOneLogo from '../assets/principle_logo.png';
import { Link } from 'react-router-dom';
import axsyLogo from '../assets/axsy_logo.png';

function Private() {
    const [activeSection, setActiveSection] = useState('about');
    const [showText, setShowText] = useState(false);
    const [expandedRisks, setExpandedRisks] = useState([]);
    const [activeTab, setActiveTab] = useState('matrix'); // 'matrix' or 'table'
    const [showTestResultsModal, setShowTestResultsModal] = useState(false);
    const [showAlgorithmResultsModal, setShowAlgorithmResultsModal] = useState(false);
    const [showSimilarityMatrixModal, setShowSimilarityMatrixModal] = useState(false);
    const [showSurveyResponsesModal, setShowSurveyResponsesModal] = useState(false);
    const [showCompanyFeedbackModal, setShowCompanyFeedbackModal] = useState(false);

    const faqSections = [
        {
            id: 'flexibility',
            title: 'Flexibility & Work Arrangements',
            content: `Research consistently shows that flexible work arrangements significantly boost both productivity and job satisfaction. When employees have the autonomy to manage their work schedules and environment, they demonstrate higher commitment and produce better quality work. Remote work options and flexible hours help reduce stress while improving work-life balance, leading to increased employee retention and overall engagement.`,
            references: `References: Hashmi, Al Ghaithi, & Sartawi (2023); Hunter (2019)`
        },
        {
            id: 'social',
            title: 'Social Preferences',
            content: `The social dynamics within a workplace play a crucial role in organizational success. Strong collaborative environments and shared social practices foster better team performance and higher job satisfaction. Companies that cultivate a cohesive social environment see improved employee retention, as team members feel more connected to the company's values and culture. Effective social integration leads to better management outcomes and stronger workforce engagement.`,
            references: `References: Morales-Huamán et al. (2023); Adham & Sukkar (2024)`
        },
        {
            id: 'workplace',
            title: 'Workplace Values & Environment',
            content: `A positive workplace environment is fundamental to organizational success. Research demonstrates that supportive workplace conditions significantly enhance employee commitment and performance. Key factors include appropriate policies, strong managerial support, and access to well-being resources. Organizations that prioritize creating healthy work environments see marked improvements in employee satisfaction, productivity, and long-term retention.`,
            references: `References: Zhenjing et al. (2022); Raziq & Maulabakhsh (2015)`
        },
        {
            id: 'workstyle',
            title: 'Work Style & Independence',
            content: `Individual work preferences and autonomy significantly impact employee performance and satisfaction. Research shows that allowing employees to customize their work environment and approach leads to higher engagement and better outcomes. The ability to choose between structured and unstructured work styles, combined with appropriate levels of independence, helps optimize both individual and team performance.`,
            references: `References: Tingo & Mseti (2022); Lee, Chong, & Ojo (2024)`
        },
        {
            id: 'career',
            title: 'Career Growth & Expectations',
            content: `Career development opportunities and clear expectations are vital for employee satisfaction and retention. Successful organizations align individual career aspirations with company objectives while providing well-defined development pathways. Balanced performance expectations that motivate without causing undue stress are crucial for maintaining both productivity and employee well-being.`,
            references: `References: Weng & Zhu (2020); Smollan & Mooney (2024)`
        }
    ];

    const operationalFitnessAreas = [
        {
            id: 'flexibility',
            icon: image1,
            title: 'Flexibility & Work Arrangements',
            description: 'Optimize how and where work gets done'
        },
        {
            id: 'social',
            icon: image2,
            title: 'Social Preferences',
            description: 'Match team dynamics and collaboration styles'
        },
        {
            id: 'workplace',
            icon: image3,
            title: 'Workplace Values',
            description: 'Align on culture and environment expectations'
        },
        {
            id: 'workstyle',
            icon: image4,
            title: 'Work Style',
            description: 'Find the right balance of structure and autonomy'
        },
        {
            id: 'growth',
            icon: image5,
            title: 'Career Growth',
            description: 'Align on career development and expectations'
        }
    ];

    // Risk assessment data
    const riskAssessmentData = [
        {
            id: 1,
            assumption: "We assume large companies (above 250 people) won't adopt our solution within the first 5 years",
            risk: "We may miss out on potential increased profits from larger companies.",
            certainty: 3,
            impact: 2,
            riskFactor: 6
        },
        {
            id: 2,
            assumption: "We assume the company sample is representative of the culture the company and candidate",
            risk: "If the sample of employees from the company that take our test is not representative, our algorithm is likely to incorrectly match candidates to the company",
            certainty: 7,
            impact: 7,
            riskFactor: 49
        },
        {
            id: 3,
            assumption: "We assume companies are willing to pay for our solution",
            risk: "If we don't acquire companies we won't have a large enough customer base for paying customers that will keep our business alive",
            certainty: 10,
            impact: 10,
            riskFactor: 100
        },
        {
            id: 4,
            assumption: "We assume our definition of 'culture' is crucial for sustaining a hire",
            risk: "If the culture is not crucial for sustaining a hire then there would be little effect on their retention rates and number of bad hires, meaning our input in the hiring process would be pointless and they are unlikely to keep paying for our service",
            certainty: 3,
            impact: 8,
            riskFactor: 24
        },
        {
            id: 5,
            assumption: "We assume we can match candidates to companies effectively based on their assessment results",
            risk: "If the assessment is not effective then there would be little effect on their retention rates and number of bad hires, meaning our input in the hiring process would be pointless and they are unlikely to keep paying for our service",
            certainty: 9,
            impact: 8,
            riskFactor: 72
        },
        {
            id: 6,
            assumption: "We assume candidates will be willing to take/have the time to do the tests",
            risk: "If candidates don't complete the tests then we don't have a user base and the solution will not work.",
            certainty: 6,
            impact: 6,
            riskFactor: 36
        },
        {
            id: 7,
            assumption: "We assume that graduates will be the most enthusiastic user group towards using our platform",
            risk: "If graduates are not the best user group to target we will be missing out on the opportunity to target a potentially better audience",
            certainty: 1,
            impact: 4,
            riskFactor: 4
        },
        {
            id: 8,
            assumption: "We assume that we'll be able to maintain our company at a reduced cost compared to competitors",
            risk: "If we are unable to maintain the reduced cost then companies will have to pay more than initially stated. If they choose not to use us because of this our business plan will fail, but this is not our main worry cost",
            certainty: 8,
            impact: 4,
            riskFactor: 32
        },
        {
            id: 9,
            assumption: "We assume we will be able to retain a company customer base by successful hires",
            risk: "If we are unable to build and maintain a large customer base our solution is still feasible but the larger the customer base, the larger the pool of matches is. This will create a better solution overall.",
            certainty: 4,
            impact: 6,
            riskFactor: 24
        }
    ];

    const toggleRiskExpansion = (id) => {
        if (expandedRisks.includes(id)) {
            // First update the UI to show the closing animation
            const rowElement = document.querySelector(`.risk-row[data-id="${id}"]`);
            if (rowElement) {
                rowElement.classList.add('closing');
                
                // After animation completes, update the state
                setTimeout(() => {
                    setExpandedRisks(expandedRisks.filter(riskId => riskId !== id));
                    // Small delay before removing the closing class for perfect animation
                    setTimeout(() => {
                        rowElement.classList.remove('closing');
                    }, 50);
                }, 500);
            } else {
                setExpandedRisks(expandedRisks.filter(riskId => riskId !== id));
            }
        } else {
            // If another row is open, close it first with animation
            if (expandedRisks.length > 0) {
                const previousRowElement = document.querySelector(`.risk-row[data-id="${expandedRisks[0]}"]`);
                if (previousRowElement) {
                    previousRowElement.classList.add('closing');
                    
                    // Perfect timing for closing one row before opening another
                    setTimeout(() => {
                        setExpandedRisks([id]);
                        // Keep the closing class a bit longer for perfect animation
                        setTimeout(() => {
                            previousRowElement.classList.remove('closing');
                        }, 100);
                    }, 400);
                } else {
                    setExpandedRisks([id]);
                }
            } else {
                setExpandedRisks([id]);
            }
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll('.faq-content-section');
            const scrollPosition = window.scrollY + window.innerHeight / 2;

            sections.forEach(section => {
                const sectionTop = section.offsetTop;
                const sectionBottom = sectionTop + section.offsetHeight;

                if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                    setActiveSection(section.id);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        // Check if we need to scroll to the focus section
        const shouldScroll = sessionStorage.getItem('scrollToFocusSection');
        
        if (shouldScroll === 'true') {
            // Find the element with the focus section
            const focusSection = document.querySelector('.ai-features-section h2');
            
            if (focusSection) {
                // Add a slight delay to ensure the page is fully rendered
                setTimeout(() => {
                    focusSection.scrollIntoView({ behavior: 'smooth' });
                    // Clear the flag
                    sessionStorage.removeItem('scrollToFocusSection');
                }, 100);
            }
        }
    }, []);

    const handleReadMore = () => {
        setShowText(true);
    };

    // Add this useEffect to handle responsive view changes
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768 && activeTab === 'matrix') {
                setActiveTab('table');
            }
        };
        
        window.addEventListener('resize', handleResize);
        // Call once on mount to set correct initial state
        handleResize();
        
        return () => window.removeEventListener('resize', handleResize);
    }, [activeTab, setActiveTab]);

    return (
        <div className="private-page">
            <Header />
            
            <div className='risk-analysis-title' style={{ padding: '20px', marginTop: '140px' }}>
                <h2>Risk Analysis</h2>
                <p>We've carefully analyzed potential risks to our business model based on key assumptions. This assessment helps us prioritize our focus and develop mitigation strategies. The following analysis provides evidence of our comprehensive risk assessment, identifying the most relevant risks and uncertainties the company will face and how we will address them through strategic prototyping and testing efforts.</p>
                <p><span className='risk-scale-note'>*numbered on a scale of 1-10 of priority</span></p>
            </div>
            
            <div className="risk-assessment-container" style={{marginTop: '-20px'}}>
                <div className="risk-view-tabs">
                    <button 
                        className={`risk-tab ${activeTab === 'matrix' ? 'active' : ''}`}
                        onClick={() => setActiveTab('matrix')}
                        data-view="matrix"
                    >
                        Matrix View
                    </button>
                    <button 
                        className={`risk-tab ${activeTab === 'table' ? 'active' : ''}`}
                        onClick={() => setActiveTab('table')}
                        data-view="table"
                    >
                        Table View
                    </button>
                </div>
                
                {activeTab === 'table' ? (
                    <div className="risk-table-container">
                        <div className="risk-table-header">
                            <div className="risk-header-cell risk-assumption">Assumptions</div>
                            <div className="risk-header-cell risk-certainty">Certainty assumption is correct</div>
                            <div className="risk-header-cell risk-impact">Impact if assumption is incorrect</div>
                            <div className="risk-header-cell risk-factor">Risk Factor</div>
                        </div>
                        
                        <div className="risk-table-body">
                            {riskAssessmentData.map((risk) => (
                                <div 
                                    key={risk.id} 
                                    className={`risk-row ${expandedRisks.includes(risk.id) ? 'expanded' : ''}`} 
                                    onClick={() => toggleRiskExpansion(risk.id)}
                                    data-id={risk.id}
                                >
                                    <div className="risk-cell risk-assumption">
                                        <span className="risk-number">{risk.id}.</span> {risk.assumption}
                                    </div>
                                    <div className="risk-cell risk-certainty">
                                        <div className="risk-score-container">
                                            <span className={`risk-score certainty-${risk.certainty >= 7 ? 'high' : risk.certainty >= 4 ? 'medium' : 'low'}`}>
                                                {risk.certainty}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="risk-cell risk-impact">
                                        <div className="risk-score-container">
                                            <span className={`risk-score impact-${risk.impact >= 7 ? 'high' : risk.impact >= 4 ? 'medium' : 'low'}`}>
                                                {risk.impact}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="risk-cell risk-factor">
                                        <div className="risk-score-container">
                                            <span className={`risk-score factor-${risk.riskFactor >= 50 ? 'high' : risk.riskFactor >= 20 ? 'medium' : 'low'}`}>
                                                {risk.riskFactor}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="risk-detail">
                                        <div className="risk-detail-inner">
                                            <h4>Risk Analysis:</h4>
                                            <p>{risk.risk}</p>
                                            <div className="risk-metrics">
                                                <div className="risk-metric">
                                                    <span>Certainty:</span> {risk.certainty}/10
                                                </div>
                                                <div className="risk-metric">
                                                    <span>Impact:</span> {risk.impact}/10
                                                </div>
                                                <div className="risk-metric">
                                                    <span>Risk Factor:</span> {risk.riskFactor} (Certainty × Impact)
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="risk-matrix-container">
                        <div className="risk-matrix">
                            <div className="matrix-content">
                                <div className="matrix-grid">
                                    {/* Grid lines */}
                                    {[...Array(10)].map((_, i) => (
                                        <div key={`h-${i}`} className="grid-line horizontal" style={{ bottom: `${i * 10}%` }} />
                                    ))}
                                    {[...Array(10)].map((_, i) => (
                                        <div key={`v-${i}`} className="grid-line vertical" style={{ left: `${i * 10}%` }} />
                                    ))}
                                    
                                    {/* Axes */}
                                    <div className="matrix-x-axis"></div>
                                    <div className="matrix-y-axis-line"></div>
                                    
                                    {/* Quadrant labels - positioned to avoid overlapping with points */}
                                    <div className="quadrant-label top-left">Certain</div>
                                    <div className="quadrant-label top-right">Uncertain</div>
                                    
                                    {/* Risk points with improved positioning and tooltip visibility */}
                                    {riskAssessmentData.map(risk => (
                                        <div 
                                            key={risk.id}
                                            className={`risk-point ${risk.riskFactor >= 50 ? 'high' : risk.riskFactor >= 20 ? 'medium' : 'low'} ${[2, 3, 4, 5].includes(risk.id) ? 'tooltip-below' : 'tooltip-above'}`}
                                            style={{ 
                                                bottom: `${(risk.impact) * 10}%`, 
                                                left: `${(risk.certainty) * 10}%`,
                                                // Adjust position to prevent overlap with edges
                                                marginTop: risk.impact > 8 ? '15px' : '0',
                                                marginLeft: (risk.certainty < 2) ? '15px' : (risk.certainty > 8) ? '-15px' : '0',
                                                // Base z-index on risk factor to prioritize important risks
                                                zIndex: Math.floor(risk.riskFactor / 2)
                                            }}
                                            onClick={() => toggleRiskExpansion(risk.id)}
                                        >
                                            {risk.id}
                                            <div className="risk-point-tooltip" style={{
                                                // Remove bottom positioning and use left positioning only
                                                left: risk.certainty > 7 ? '30%' : risk.certainty < 3 ? '70%' : '50%',
                                                // Adjust width for different content lengths
                                                width: risk.assumption.length > 100 ? '350px' : '300px'
                                            }}>
                                                <strong>Assumption {risk.id}:</strong> {risk.assumption}
                                                <br />
                                                <strong>Risk Factor:</strong> {risk.riskFactor}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Y-axis labels */}
                                <div className="matrix-y-axis">
                                    <div className="matrix-ticks">
                                        <div className="matrix-tick">10</div>
                                        <div className="matrix-tick">9</div>
                                        <div className="matrix-tick">8</div>
                                        <div className="matrix-tick">7</div>
                                        <div className="matrix-tick">6</div>
                                        <div className="matrix-tick">5</div>
                                        <div className="matrix-tick">4</div>
                                        <div className="matrix-tick">3</div>
                                        <div className="matrix-tick">2</div>
                                        <div className="matrix-tick">1</div>
                                    </div>
                                </div>
                                
                                <div className="matrix-label y-top">Critical</div>
                                <div className="matrix-label y-bottom">Not Critical</div>
                            </div>
                            
                            {/* Improved X-axis labels alignment */}
                            <div className="matrix-x-axis-labels">
                                <div className="matrix-ticks x-ticks">
                                    {[...Array(9)].map((_, i) => (
                                        <div key={`x-${i+1}`} className="matrix-tick">{i+1}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Enhanced legend */}
                        <div className="matrix-legend">
                            <div className="legend-item">
                                <span className="legend-color low"></span>
                                <span className="legend-text">Low Risk (RF &lt; 20)</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-color medium"></span>
                                <span className="legend-text">Medium Risk (RF 20-49)</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-color high"></span>
                                <span className="legend-text">High Risk (RF ≥ 50)</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className='risk-analysis-title' style={{ padding: '20px', marginTop: '-70px', marginBottom: '20px'}}>
                {/* <h2>Risk Analysis</h2> */}
                <p>The highest risk assumptions were 2, 3, 5 and 6 so we decided to test these assumptions to validate that our solution would meet the user's needs. The table below shows how we aim to address each of these assumptions through prototyping and testing efforts.</p>
            </div>

            <div className="assumption-testing-container">
                <h2 className="section-title">Chosen Assumption Testing</h2>
                
                <div className="assumption-cards">
                    <div className="assumption-card">
                        <div className="assumption-header">
                            <div className="assumption-number">2</div>
                            <h3>Company Sample Representation</h3>
                        </div>
                        <div className="assumption-content">
                            <div className="assumption-section">
                                <h4>Hypothesis</h4>
                                <p>We hypothesize that the test will produce an average of 70% cultural match between employees within the companies</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Test Method</h4>
                                <p>Employees from Axsy and principle one will complete the survey and the similarity of their results will be compared</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Results</h4>
                                <p className="test-result">The two companies that completed our survey got matched with grads that had previously interned there and fit in well - highlighting that our algorithm could match their cultures effectively.</p>
                                <button 
                                    className="view-results-button"
                                    onClick={() => setShowSimilarityMatrixModal(true)}
                                >
                                    View Similarity Matrix
                                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="1.5" fill="none">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="3" y1="9" x2="21" y2="9"></line>
                                        <line x1="3" y1="15" x2="21" y2="15"></line>
                                        <line x1="9" y1="3" x2="9" y2="21"></line>
                                        <line x1="15" y1="3" x2="15" y2="21"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="assumption-section">
                                <h4>Impact on Quokka</h4>
                                <p>These percentage results will help inform our algorithm to identify the parameters of a good match and of the same answers. From these results we know the sample from the company is representative of the company as a whole. If in the case the company wants to create profiles for their employees we have prepared the option for the company to make multiple profiles for different departments/roles to combat this</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="assumption-card">
                        <div className="assumption-header">
                            <div className="assumption-number">3</div>
                            <h3>Company Willingness to Pay</h3>
                        </div>
                        <div className="assumption-content">
                            <div className="assumption-section">
                                <h4>Hypothesis</h4>
                                <p>We hypothesise that SMEs will see value in our MVP and be willing to pay the price we have set for our product</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Test Method</h4>
                                <p>Contact companies and use our demo measure interest</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Results</h4>
                                <p className="test-result">Two companies that tested our MVP confirmed they would be willing to pay for our service, with AXSY specifically stating they would pay £1000 per company profile.</p>
                                <button 
                                    className="view-results-button"
                                    onClick={() => setShowCompanyFeedbackModal(true)}
                                >
                                    View Company Feedback
                                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="1.5" fill="none">
                                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                    </svg>
                                </button>
                            </div>
                            <div className="assumption-section">
                                <h4>Impact on Quokka</h4>
                                <p>We will only target companies over 20 employees to ensure they need their needs and they will see the value in using our product</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="assumption-card">
                        <div className="assumption-header">
                            <div className="assumption-number">5</div>
                            <h3>Effective Matching Algorithm</h3>
                        </div>
                        <div className="assumption-content">
                            <div className="assumption-section">
                                <h4>Hypothesis</h4>
                                <p>We hypothesise that our algorithm will be able to match respondents by the answers of thier survey questions</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Test Method</h4>
                                <p>Send survey to a pool of graduates and companies and see if the algorithm can match candidates that have previously worked / interned at these companies. Through our research how the questions we have chosen in our survey are important in assessing 'operational fit'</p>
                                <div className="results-buttons">
                                    <button 
                                        className="view-results-button"
                                        onClick={() => setShowTestResultsModal(true)}
                                    >
                                        View Survey Questions
                                        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <line x1="10" y1="14" x2="21" y2="3"></line>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="assumption-section">
                                <h4>Results</h4>
                                <p className="test-result">The questions we have selected are grounded in research and specifically designed to assess operational fit in the workplace, recognising its crucial impact on productivity and employee well-being.</p>
                                <button 
                                        className="view-results-button"
                                        onClick={() => setShowAlgorithmResultsModal(true)}
                                    >
                                        View Algorithm Results
                                        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                            <polyline points="14 2 14 8 20 8"></polyline>
                                            <line x1="16" y1="13" x2="8" y2="13"></line>
                                            <line x1="16" y1="17" x2="8" y2="17"></line>
                                            <polyline points="10 9 9 9 8 9"></polyline>
                                        </svg>
                                    </button>
                            </div>
                            <div className="assumption-section">
                                <h4>Impact on Quokka</h4>
                                <p>We are confident in our MVP - our way of assessing organisational fit through our surveys is valid along with the way we match candidates to companies</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="assumption-card">
                        <div className="assumption-header">
                            <div className="assumption-number">6</div>
                            <h3>Candidate Participation</h3>
                        </div>
                        <div className="assumption-content">
                            <div className="assumption-section">
                                <h4>Hypothesis</h4>
                                <p>We hypothesise that 30% of the graduates we reached out too will be willing to take the time to do the test</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Test Method</h4>
                                <p>Ask our sample of DE4 to complete the survey, explain what quokka is doing and see how many people are willing to take time out of their day to complete it</p>
                            </div>
                            <div className="assumption-section">
                                <h4>Results</h4>
                                <p className="test-result">30/90 members of DE4 completed our survey, confirming our hypothesis that at least a third of candidates would participate.</p>
                                <button 
                                    className="view-results-button"
                                    onClick={() => setShowSurveyResponsesModal(true)}
                                >
                                    View Survey Analytics
                                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="1.5" fill="none">
                                        <path d="M12 20V10"></path>
                                        <path d="M18 20V4"></path>
                                        <path d="M6 20v-6"></path>
                                        <rect x="2" y="2" width="20" height="20" rx="2"></rect>
                                    </svg>
                                </button>
                            </div>
                            <div className="assumption-section">
                                <h4>Impact on Quokka</h4>
                                <p>A third of our cohort took the time to do our tests in the short period of 25 days given the the opportunity of being matched to a company for a grad role- this shows that grads are intrinsically motivated to sign up to our platform, meaning we will have a big pool of graduates to match on our system</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="ai-features-section" style={{width: '80%'}}>
                            <h2 >Inference Areas</h2>
                            <p style={{textAlign: 'center', paddingLeft: '10%', paddingRight: '10%'}}>At Quokka, we focus on operational fit which has been proven to be a large reason for unsuccessful hires. Our assessment is a simple first-stage filtering process to bring you the best candidates.</p>
                            <div className="ai-features-container">
                                <div className="ai-feature-card">
                                    <div className="ai-feature-icon">
                                        <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <path d="M12 8v4l3 3"></path>
                                            <circle cx="12" cy="12" r="10"></circle>
                                        </svg>
                                    </div>
                                    <h3>Flexibility</h3>
                                    <p>The importance of flexible work schedules and environments e.g. flexible hours, remote work options etc.</p>
                                    <Link to="/inferences-table" style={{marginTop: '50px'}} className="learn-more-link">Learn more →</Link>
                                </div>
                                
                                <div className="ai-feature-card">
                                    <div className="ai-feature-icon">
                                        <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                    </div>
                                    <h3>Social Preferences</h3>
                                    <p>How individuals prefer to interact within the workplace, including their inclination toward teamwork, socialising with colleagues etc.</p>
                                    <Link to="/inferences-table" className="learn-more-link">Learn more →</Link>
                                </div>
                                
                                <div className="ai-feature-card">
                                    <div className="ai-feature-icon">
                                        <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <path d="M3 6l9 6 9-6"></path>
                                            <path d="M21 15a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v6z"></path>
                                        </svg>
                                    </div>
                                    <h3>Workplace Values</h3>
                                    <p>The principles and atmosphere that define a workplace, including formal policies like dress codes etc.</p>
                                    <Link to="/inferences-table" className="learn-more-link">Learn more →</Link>
                                </div>

                                <div className="ai-feature-card">
                                    <div className="ai-feature-icon">
                                        <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                        </svg>
                                    </div>
                                    <h3>Work Style</h3>
                                    <p>Explores individual approaches to work, including preferences for structured versus unstructured tasks, the degree of autonomy desired etc.</p>
                                    <Link to="/inferences-table" style={{marginTop: '20px'}} className="learn-more-link">Learn more →</Link>
                                </div>

                                <div className="ai-feature-card">
                                    <div className="ai-feature-icon">
                                        <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="1.5" fill="none">
                                            <path d="M12 20V10"></path>
                                            <path d="M18 20V4"></path>
                                            <path d="M6 20v-6"></path>
                                        </svg>
                                    </div>
                                    <h3>Career Growth</h3>
                                    <p>Addresses an individual's aspirations regarding professional development, advancement opportunities etc.</p>
                                    <Link to="/inferences-table" className="learn-more-link">Learn more →</Link>
                                </div>
                            </div>
                        </div>

            <div className="feature-boxes-container" style={{display: 'none'}}>
                <h2 className="section-title">What we're testing</h2>
                
                <div className="feature-boxes">
                    <div className="feature-box">
                        <h3>Flexibility & Work Arrangements</h3>
                        <p>The importance of flexible work schedules and environments e.g. flexible hours, remote work options, and the ability to manage personal responsibilities alongside professional duties</p>
                        <div className="feature-icon flexibility-icon"></div>
                        <a href="/inferences-table" className="learn-more-link">Learn more →</a>
                    </div>
                    
                    <div className="feature-box">
                        <h3>Social Preferences</h3>
                        <p>How individuals prefer to interact within the workplace, including their inclination toward teamwork, socialising with colleagues, and overall integration into the company culture.</p>
                        <div className="feature-icon social-icon"></div>
                        <a href="/inferences-table" className="learn-more-link">Learn more →</a>
                    </div>
                    
                    <div className="feature-box">
                        <h3>Workplace Values & Environment</h3>
                        <p>The principles and atmosphere that define a workplace, including formal policies like dress codes, the level of managerial support for employee well-being, and the availability of mental health resources.</p>
                        <div className="feature-icon workplace-icon"></div>
                        <a href="/inferences-table" className="learn-more-link">Learn more →</a>
                    </div>
                    
                    <div className="feature-box">
                        <h3>Work Style & Independence</h3>
                        <p>Explores individual approaches to work, including preferences for structured versus unstructured tasks, the degree of autonomy desired, and adaptability to varying work conditions.</p>
                        <div className="feature-icon workstyle-icon"></div>
                        <a href="/inferences-table" className="learn-more-link">Learn more →</a>
                    </div>
                    
                    <div className="feature-box">
                        <h3>Career Growth & Expectations</h3>
                        <p>Addresses an individual's aspirations regarding professional development, advancement opportunities, and expectations for organisational support in achieving career goals.</p>
                        <div className="feature-icon career-icon"></div>
                        <a href="/inferences-table" className="learn-more-link">Learn more →</a>
                    </div>
                </div>
            </div>
            
            <div className={`test-results-modal ${showTestResultsModal ? 'active' : ''}`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Assumption 5 Test Results</h3>
                        <button 
                            className="close-modal"
                            onClick={() => setShowTestResultsModal(false)}
                        >
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="1.5" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="test-results-table-wrapper">
                            <table className="test-results-table">
                                <thead>
                                    <tr>
                                        <th>Candidate Survey Questions</th>
                                        <th>Inferences</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Having flexible work hours is really important to me</td>
                                        <td>Indicates preference for work-life balance or structured schedules</td>
                                    </tr>
                                    <tr>
                                        <td>You are happy to work overtime to further my career without specific compensation</td>
                                        <td>Determination to progress quickly in career and attitude towards high pressure situations - also indicates work/life balance</td>
                                    </tr>
                                    <tr>
                                        <td>How many days a week would you prefer to be in the office?</td>
                                        <td>Gives an impression of how highly the candidate prioritises socialising with colleagues</td>
                                    </tr>
                                    <tr>
                                        <td>It is really important to have flexibility regarding working from home vs working in the office</td>
                                        <td>How highly the candidate prioritises socialising with colleagues and balance work/life schedule</td>
                                    </tr>
                                    <tr>
                                        <td>You always like to eat lunch with your colleagues when you are in the office</td>
                                        <td>How highly the candidate prioritises socialising with colleagues</td>
                                    </tr>
                                    <tr>
                                        <td>Having a formal dress code is important to you</td>
                                        <td>How highly professionalism is valued</td>
                                    </tr>
                                    <tr>
                                        <td>You would always choose to work with a team rather than individually</td>
                                        <td>How important control is to the candidate and their attitude to external opinions</td>
                                    </tr>
                                    <tr>
                                        <td>You like to socialise with your colleagues outside of work hours</td>
                                        <td>How integrated the candidate wants to be in the company</td>
                                    </tr>
                                    <tr>
                                        <td>How do you prefer to communicate with colleagues?</td>
                                        <td>Introvert/extrovert, how focussed and direct the candidate is</td>
                                    </tr>
                                    <tr>
                                        <td>What time of day do you feel most productive?</td>
                                        <td>How you work</td>
                                    </tr>
                                    <tr>
                                        <td>You feel strongly about the importance of being able to give and receive feedback effectively at work</td>
                                        <td>How involved the candidate wants to be in the company</td>
                                    </tr>
                                    <tr>
                                        <td>How often do you expect to have catch-ups with your manager or mentor?</td>
                                        <td>How much you need guidance when completing a task - time that the company has to invest in that person</td>
                                    </tr>
                                    <tr>
                                        <td>How do you handle high-pressure situations with direct or intense communication?</td>
                                        <td>Introvert / extrovert, how resilient and adaptable the candidate is</td>
                                    </tr>
                                    <tr>
                                        <td>You are happy to take on a project with minimal assistance or guidance</td>
                                        <td>How independent the candidate is, How much time will need to be spent on this candidate</td>
                                    </tr>
                                    <tr>
                                        <td>It is important for your workplace to offer flexibility, such as the ability to step out during the day (e.g. for a walk)</td>
                                        <td>Attitude towards professionalism and preference regarding structure</td>
                                    </tr>
                                    <tr>
                                        <td>How often are you comfortable with taking on tasks that differ from those outlined directly in your job description?</td>
                                        <td>Attitude towards career progression and responsibility</td>
                                    </tr>
                                    <tr>
                                        <td>You prefer a structured mentoring system (e.g., an assigned senior mentor for guidance on career progression and workplace challenges), as opposed to managing your career development independently</td>
                                        <td>Attitude towards career progression</td>
                                    </tr>
                                    <tr>
                                        <td>Your manager must demonstrate empathy and support for your well-being as opposed to having a purely professional relationship</td>
                                        <td>Level in which candidate values support</td>
                                    </tr>
                                    <tr>
                                        <td>You desire a working environment where you are permitted to listen to music</td>
                                        <td>Attitude towards professionalism</td>
                                    </tr>
                                    <tr>
                                        <td>What type of mental health support do you value most in a workplace?</td>
                                        <td>Level in which candidate values support</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button 
                            className="close-button"
                            onClick={() => setShowTestResultsModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            <div className={`test-results-modal ${showAlgorithmResultsModal ? 'active' : ''}`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Assumption 5 Algorithm Results</h3>
                        <button 
                            className="close-modal"
                            onClick={() => setShowAlgorithmResultsModal(false)}
                        >
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="1.5" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body" >
                        <div className="algorithm-results-wrapper" >
                            <h4 className="algorithm-results-title">Assumption 5 Test Results</h4>
                            
                            <div className="company-test-case">
                                <div className="company-header">
                                    <h5>Company: AXSY</h5>
                                </div>
                                
                                <div className="matching-results">
                                    <div className="matching-column">
                                        <h6>Top Matches from MVP matching algorithm</h6>
                                        <div className="code-block">
                                            <pre>
{`{
  "match_percentage": 76.1,
  "name": "Eesha Sondhi"
},
{
  "match_percentage": 73.3,
  "name": "Kerem Bocuoglu"
},
{
  "match_percentage": 73.3,
  "name": "Yasmin Zaraket"
}`}
                                            </pre>
                                        </div>
                                        
                                        <h6 className="bottom-match">Bottom Match from MVP</h6>
                                        <div className="code-block">
                                            <pre>
{`{
  "match_percentage": 52.8,
  "name": "Lena Westerburg Burr"
}`}
                                            </pre>
                                        </div>
                                    </div>
                                    
                                    <div className="process-column">
                                        <div className="process-step">
                                            <p>We then sent the CVs of these top 3 candidates to the CEO of AXSY along with the bottom match to test the validity of the algorithm</p>
                                            <div className="arrow-right"></div>
                                        </div>
                                        
                                        <div className="process-result">
                                            <p>CEO of Axsy confirmed he would take forward these 3 grads into a technical stage interview and he would not take through the bottom match, successfully validating our assumption</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="company-test-case">
                                <div className="company-header">
                                    <h5>Company: Principle One</h5>
                                </div>
                                
                                <div className="matching-results">
                                    <div className="matching-column">
                                        <h6>Top Matches from MVP matching algorithm</h6>
                                        <div className="code-block">
                                            <pre>
{`{
  "match_percentage": 80.9,
  "name": "Zubayr Ghufoor"
},
{
  "match_percentage": 80.9,
  "name": "Ruslan Patel"
},
{
  "match_percentage": 79.3,
  "name": "Shreya Jei"
}`}
                                            </pre>
                                        </div>
                                        
                                        <h6 className="bottom-match">Bottom Match from MVP</h6>
                                        <div className="code-block">
                                            <pre>
{`{
  "match_percentage": 44.1,
  "name": "Callum Hargrove"
}`}
                                            </pre>
                                        </div>
                                    </div>
                                    
                                    <div className="process-column">
                                        <div className="process-step">
                                            <p>We then sent the CVs of these top 3 candidates to Hiring Manager at Principle one along with the bottom Match to test the validity of the algorithm</p>
                                            <div className="arrow-right"></div>
                                        </div>
                                        
                                        <div className="process-result">
                                            <p>Hiring Manager at Principle One confirmed he would take forward the top 2 of the 3 grads into a technical stage interview and he would not take through the bottom match, successfully validating our assumption</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="blind-test-note">
                                <p>Both candidates who had experience at their respective companies were among the top matches. The entire process was conducted blindly, ensuring they did not see the companies' results.</p>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button 
                            className="close-button"
                            onClick={() => setShowAlgorithmResultsModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            <div className={`test-results-modal ${showSimilarityMatrixModal ? 'active' : ''}`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Assumption 2 Similarity Matrix Results</h3>
                        <button 
                            className="close-modal"
                            onClick={() => setShowSimilarityMatrixModal(false)}
                        >
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="1.5" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="similarity-matrix-wrapper">
                            <div className="company-test-case">
                                <div className="company-header">
                                    <h5>Company 1: Principle One</h5>
                                </div>
                                
                                <div className="matrix-container">
                                    <div className="matrix-visualization">
                                        <div className="matrix-code-block">
                                            <pre>
{`Complete Similarity Matrix:
      Emp00  Emp01  Emp02  Emp03  Emp04  Emp05  Emp06  Emp07  Emp08
Emp00  1.000  0.670  0.675  0.668  0.612  0.737  0.676  0.709  0.595
Emp01  0.670  1.000  0.720  0.748  0.813  0.912  0.788  0.868  0.717
Emp02  0.675  0.720  1.000  0.614  0.733  0.720  0.611  0.762  0.646
Emp03  0.668  0.748  0.614  1.000  0.809  0.709  0.721  0.826  0.696
Emp04  0.612  0.813  0.733  0.809  1.000  0.735  0.633  0.850  0.749
Emp05  0.737  0.912  0.720  0.709  0.735  1.000  0.825  0.900  0.812
Emp06  0.676  0.788  0.611  0.721  0.633  0.825  1.000  0.752  0.707
Emp07  0.709  0.868  0.762  0.826  0.850  0.900  0.752  1.000  0.738
Emp08  0.595  0.717  0.646  0.696  0.749  0.812  0.707  0.738  1.000`}
                                            </pre>
                                        </div>
                                        
                                        <div className="matrix-metrics">
                                            <pre>
{`Overall Similarity Metrics:
average_similarity: 0.735
min_similarity: 0.595
max_similarity: 0.912
std_similarity: 0.080`}
                                            </pre>
                                        </div>
                                    </div>
                                    
                                    <div className="matrix-analysis">
                                        <h6>Average Similarity (0.735 or 73.5%):</h6>
                                        <ul>
                                            <li>This suggests a strong shared cultural understanding</li>
                                            <li>Indicates that averaging responses would be more valid as a representative sample</li>
                                            <li>Shows enough alignment to justify using aggregated results</li>
                                        </ul>
                                        
                                        <h6>Range of Similarities:</h6>
                                        <ul>
                                            <li>Minimum: 0.595 (59.5%) - Even the most different employees share majority alignment</li>
                                            <li>Maximum: 0.912 (91.2%) - Some employees are very highly aligned</li>
                                            <li>All pairs are above 50% similar, supporting the idea of a coherent culture</li>
                                        </ul>
                                        
                                        <h6>Looking at individual relationships:</h6>
                                        <ul>
                                            <li>Employee pairs like Emp01-Emp05 (0.912) and Emp05-Emp07 (0.900) show very strong alignment</li>
                                            <li>Even the lowest similarity (Emp00-Emp08 at 0.595) shows majority agreement</li>
                                            <li>Many pairs show {'>'}70% similarity, suggesting consistent cultural views</li>
                                        </ul>
                                        
                                        <h6>Conclusion:</h6>
                                        <p>There's enough cultural cohesion to justify using averages as representative. The high minimum similarity (59.5%) suggests core cultural values are shared. The relatively small standard deviation (0.080) indicates consistent responses.</p>
                                        <p>This data would support using averaged responses as representative of the company culture, since the similarity scores suggest strong alignment across employees.</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="company-test-case">
                                <div className="company-header">
                                    <h5>Company 2: AXSY</h5>
                                </div>
                                
                                <div className="matrix-container">
                                    <div className="matrix-visualization">
                                        <div className="matrix-code-block">
                                            <pre>
{`Complete Similarity Matrix:
      Emp00  Emp01  Emp02  Emp03  Emp04  Emp05  Emp06  Emp07  Emp08  Emp09  Emp10  Emp11
Emp00  1.000  0.514  0.718  0.621  0.579  0.668  0.610  0.682  0.706  0.628  0.735  0.801
Emp01  0.514  1.000  0.623  0.675  0.638  0.747  0.656  0.628  0.701  0.680  0.580  0.544
Emp02  0.718  0.623  1.000  0.790  0.685  0.708  0.807  0.790  0.785  0.832  0.656  0.706
Emp03  0.621  0.675  0.790  1.000  0.708  0.764  0.715  0.680  0.762  0.687  0.660  0.635
Emp04  0.579  0.638  0.685  0.708  1.000  0.745  0.767  0.735  0.739  0.741  0.574  0.588
Emp05  0.668  0.747  0.708  0.764  0.745  1.000  0.788  0.687  0.766  0.657  0.654  0.657
Emp06  0.610  0.656  0.807  0.715  0.767  0.788  1.000  0.706  0.789  0.779  0.634  0.662
Emp07  0.682  0.628  0.790  0.680  0.735  0.687  0.706  1.000  0.757  0.769  0.644  0.619
Emp08  0.706  0.701  0.785  0.762  0.739  0.766  0.789  0.757  1.000  0.802  0.724  0.730
Emp09  0.628  0.680  0.832  0.687  0.741  0.657  0.779  0.769  0.802  1.000  0.593  0.673
Emp10  0.735  0.580  0.656  0.660  0.574  0.654  0.634  0.644  0.724  0.593  1.000  0.681
Emp11  0.801  0.544  0.706  0.635  0.588  0.657  0.662  0.619  0.730  0.673  0.681  1.000`}
                                            </pre>
                                        </div>
                                        
                                        <div className="matrix-metrics">
                                            <pre>
{`Overall Similarity Metrics:
average_similarity: 0.693
min_similarity: 0.514
max_similarity: 0.832
std_similarity: 0.070`}
                                            </pre>
                                        </div>
                                    </div>
                                    
                                    <div className="matrix-analysis">
                                        <h6>Overall Metrics:</h6>
                                        <ul>
                                            <li>Average similarity: 0.693 (69.3%) - Shows moderately strong cultural alignment</li>
                                            <li>Minimum similarity: 0.514 (51.4%) - Between Emp00 and Emp01</li>
                                            <li>Maximum similarity: 0.832 (83.2%) - Between Emp02 and Emp09</li>
                                            <li>Standard deviation: 0.070 - Indicates fairly consistent similarity scores</li>
                                        </ul>
                                        
                                        <h6>Notable Patterns:</h6>
                                        <ul>
                                            <li>Some strong alignments:
                                                <ul>
                                                    <li>Emp02-Emp06 (0.807)</li>
                                                    <li>Emp00-Emp11 (0.801)</li>
                                                    <li>Emp02-Emp09 (0.832)</li>
                                                </ul>
                                            </li>
                                            <li>Some weaker alignments:
                                                <ul>
                                                    <li>Emp00-Emp01 (0.514)</li>
                                                    <li>Emp01-Emp11 (0.544)</li>
                                                    <li>Emp04-Emp10 (0.574)</li>
                                                </ul>
                                            </li>
                                        </ul>
                                        
                                        <h6>What This Suggests:</h6>
                                        <ul>
                                            <li>The company has a moderately cohesive culture (69.3% average similarity)</li>
                                            <li>Even the most different employees share majority views ({'>'}51% similarity)</li>
                                            <li>There might be some subgroups or clusters of employees with very similar perspectives</li>
                                            <li>The relatively low standard deviation (0.070) suggests consistent cultural alignment across the company</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button 
                            className="close-button"
                            onClick={() => setShowSimilarityMatrixModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            <div className={`test-results-modal ${showSurveyResponsesModal ? 'active' : ''}`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Assumption 6 Survey Participation Results</h3>
                        <button 
                            className="close-modal"
                            onClick={() => setShowSurveyResponsesModal(false)}
                        >
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="1.5" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="survey-responses-wrapper">
                            <div className="survey-overview-card">
                                <h4>Responses Overview</h4>
                                <div className="metrics-container">
                                    <div className="metric-box">
                                        <div className="metric-icon">
                                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="#4F6BFF" strokeWidth="1.5" fill="none">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                        </div>
                                        <div className="metric-content">
                                            <span className="metric-value">30</span>
                                            <span className="metric-label">Responses</span>
                                        </div>
                                    </div>
                                    
                                    <div className="metric-box">
                                        <div className="metric-icon">
                                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="#4F6BFF" strokeWidth="1.5" fill="none">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="12 6 12 12 16 14"></polyline>
                                            </svg>
                                        </div>
                                        <div className="metric-content">
                                            <span className="metric-value">03:48</span>
                                            <span className="metric-label">Average Time</span>
                                        </div>
                                    </div>
                                    
                                    <div className="metric-box">
                                        <div className="metric-icon">
                                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="#4F6BFF" strokeWidth="1.5" fill="none">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                        </div>
                                        <div className="metric-content">
                                            <span className="metric-value">25</span>
                                            <span className="metric-label">Days</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="survey-analysis">
                                <h4>Participation Analysis</h4>
                                
                                <div className="analysis-section">
                                    <h5>Key Findings</h5>
                                    <ul>
                                        <li>33.3% response rate (30 out of 90 DE4 students)</li>
                                        <li>Average completion time of 3 minutes and 48 seconds</li>
                                        <li>Survey was active for 25 days</li>
                                        <li>All respondents completed the entire survey with no dropouts</li>
                                    </ul>
                                </div>
                                
                                <div className="analysis-section">
                                    <h5>Completion Rate by Question Type</h5>
                                    <div className="completion-chart">
                                        <div className="chart-bar">
                                            <div className="bar-label">Work Preferences</div>
                                            <div className="bar-container">
                                                <div className="bar-fill" style={{width: '100%'}}></div>
                                            </div>
                                            <div className="bar-value">100%</div>
                                        </div>
                                        <div className="chart-bar">
                                            <div className="bar-label">Social Dynamics</div>
                                            <div className="bar-container">
                                                <div className="bar-fill" style={{width: '100%'}}></div>
                                            </div>
                                            <div className="bar-value">100%</div>
                                        </div>
                                        <div className="chart-bar">
                                            <div className="bar-label">Management Style</div>
                                            <div className="bar-container">
                                                <div className="bar-fill" style={{width: '100%'}}></div>
                                            </div>
                                            <div className="bar-value">100%</div>
                                        </div>
                                        <div className="chart-bar">
                                            <div className="bar-label">Career Growth</div>
                                            <div className="bar-container">
                                                <div className="bar-fill" style={{width: '100%'}}></div>
                                            </div>
                                            <div className="bar-value">100%</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="analysis-section">
                                    <h5>Conclusion</h5>
                                    <p>The survey participation rate of 33.3% validates our hypothesis that at least one-third of candidates would be willing to complete the survey. The average completion time of under 4 minutes indicates the survey length is appropriate and not overly burdensome for participants.</p>
                                    <p>The 100% completion rate across all question types suggests the survey was engaging and well-designed, with no sections causing participants to abandon the process. This data confirms that our survey design is effective and that candidates are willing to invest time in a process that could help them find better cultural fits in their job search.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button 
                            className="close-button"
                            onClick={() => setShowSurveyResponsesModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
            
            <div className={`test-results-modal ${showCompanyFeedbackModal ? 'active' : ''}`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Assumption 3 Company Feedback</h3>
                        <button 
                            className="close-modal"
                            onClick={() => setShowCompanyFeedbackModal(false)}
                        >
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="1.5" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="company-feedback-wrapper" style={{overflowY: 'auto'}}>
                            <div className="feedback-letter">
                                <div className="letter-header">
                                    <div className="company-logo">
                                        <img 
                                            src={axsyLogo} 
                                            alt="AXSY Logo" 
                                            style={{ maxHeight: '40px' }}
                                        />
                                    </div>
                                </div>
                                <div className="letter-content">
                                    <p>Dear Team Quokka,</p>
                                    
                                    <p>Thank you for sharing the candidate-company match results with us. Having seen your matching process demo, I appreciate the structured approach to identifying potential fits and can see the value in the system.</p>
                                    
                                    <p>After reviewing the assessments, I can confirm that if we were hiring, we would move forward with three of the highest-matching graduates to the next stage of our technical interview process. Conversely, we would not proceed with the lowest-matching candidate, as their profile does not align with our requirements.</p>
                                    
                                    <p>While this outcome reinforces the effectiveness of your approach, we would need a trial period of a few months to assess how well the system performs in a real hiring scenario before fully committing to it.</p>
                                    
                                    <p>If the platform continues to deliver high-quality matches that reduce our reliance on recruiters, we would be willing to pay £1000 per company profile.</p>
                                    
                                    <p>We appreciate the opportunity to be part of this process and look forward to exploring this further.</p>
                                    
                                    <p className="signature">Best, Sanjay.</p>
                                    
                                    <p className="signature-title">CEO, Founder | m: +44.7850.037406 (UK) | m: (650) 618-3916 (US) | <a href="http://www.axsy.com" target="_blank" rel="noopener noreferrer">www.axsy.com</a></p>
                                </div>
                            </div>
                            
                            <div className="feedback-letter">
                                <div className="letter-header">
                                    <div className="company-logo">
                                        <img 
                                            src={principleOneLogo} 
                                            alt="Principle One Logo" 
                                            style={{ maxHeight: '40px' }}
                                        />
                                    </div>
                                </div>
                                <div className="letter-content">
                                    <p className="subject">Subject: Feedback on Candidate-Company Match Results</p>
                                    
                                    <p>Dear Team Quokka,</p>
                                    
                                    <p>Thank you for sharing the candidate-company match results with us.</p>
                                    
                                    <p>After reviewing your matching process demo, I appreciate the structured approach you take in identifying potential candidate fits. The process is well-designed, and I trust its ability to streamline talent selection effectively.</p>
                                    
                                    <p>Based on the assessments provided, I can confirm that if we were currently hiring, based on their CV's and results, we would move forward with one of the highest-matching graduates for the next stage of our technical interview process. Conversely, the lowest-matching candidate's profile does not align with our requirements, reinforcing the accuracy of your system in filtering suitable candidates.</p>
                                    
                                    <p>This outcome highlights the effectiveness of your approach in matching candidates to our company's needs. We value the opportunity to participate in this process and look forward to exploring potential collaboration in the future.</p>
                                    
                                    <p className="signature">Best wishes,</p>
                                    
                                    <p className="signature">Rabia Dhami,<br />HR,<br />Principle One Ltd,</p>
                                    
                                    <p><a href="http://www.principleone.co.uk" target="_blank" rel="noopener noreferrer">www.principleone.co.uk</a></p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button 
                            className="close-button"
                            onClick={() => setShowCompanyFeedbackModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Private;
